/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

import {
    CartActions,
    NotificationActions,
    useFlags,
    zipCodeValidator,
} from 'sarsaparilla';
import { useRecaptchaWithFallback } from 'shared-ui/recaptcha';
import {
    createReservations,
    fetchFacilityByIdOrSlug,
    fetchPassTypes,
    removeReservation,
    validateReservation,
} from 'ui-sitepass/src/actions';
import {
    facilityByIdOrSlugSelectors,
    passTypesSelectors,
    validationSelectors,
} from 'ui-sitepass/src/reducers';
import { isSitePassPurchasableOnDate } from 'ui-sitepass/src/utils/date_time_utils';
import { RECAPTCHA_ERROR, RECAPTCHA_ERROR2 } from 'ui-sitepass/src/constants/recaptcha';

import { fetchFacilityData } from '../../actions/facilityDataActions';
import { fetchToursByFacility } from '../../actions/toursDataActions';
import {
    applyPendingModifications,
    discardPendingModifications,
    getMyPasses,
    getMyReservations,
    getOrderDetailsView,
    openModifyModal,
    openWarningPassModal,
    removeFromCart,
    resetSitePass,
    saveChangesToAccount,
    toggleNeedToKnowCheckbox,
    toggleSitePassInfoCheckbox,
    updateWithTicketHolderDetails,
} from '../../actions/orderDetailsActions';
import { FACILITY_SITEPASS, HIDDEN_SITE_PASS_TYPES } from '../../constants';
import { showSitePasses } from '../../utils/launchDarklyUtils';
import { errorObject, errorSectionHeader } from '../../utils/sitePassUtils';

const useOrderDetails = (reservationId, isOwnService = true, useSitePass = false) => {
    const { facilitiesUsingSitePassIntegration } = useFlags();
    const validationRefs = useRef({});
    const validationErrorsRefs = useRef({});
    const [useOrderDetailsState, setUseOrderDetailsState] = useState({
        updateErrors: false,
        validationErrors: null,
    });
    const navigate = useNavigate();

    const { showDigitalPassDiscountsV2 } = useFlags();

    const dispatch = useDispatch();
    const isFacilityDataLoading = useSelector(
        (state) => state?.timedEntry?.facility?.isLoading
    );
    const isToursLoading = useSelector((state) => state?.timedEntry?.tours?.isLoading);
    const orderDetailsData = useSelector((state) => state?.timedEntry?.orderDetails);
    const tourImagesData = useSelector((state) => state?.timedEntry?.tourImages?.data);
    const cartReservations = useSelector((state) => state?.cart?.reservations) || [];
    const {
        reservation,
        facility_id: facilityId,
        tour_id: tourId,
        isLoading: isOrderDetailsLoading,
        isNeedToKnowChecked,
        isSitePassInfoChecked,
        hasBeenModified,
        shouldSaveToAccount,
        ticketHolderDetails,
        my_passes: myPasses,
        my_reservations: myReservations,
        sitePass: { reservations: sitePassReservations, sitePassRemovedReservationIds },
    } = orderDetailsData;

    const showDigitalPassDiscounts = useMemo(() => {
        if (!showDigitalPassDiscountsV2) {
            return false;
        }

        if (showDigitalPassDiscountsV2[facilityId]) {
            return true;
        }
        return showDigitalPassDiscountsV2['default'];
    }, [showDigitalPassDiscountsV2, facilityId]);

    const selectedDate = useSelector((state) => state.timedEntry.filters.date);

    const tour = useSelector((state) =>
        state?.timedEntry?.tours?.tourList?.find(
            (tourItem) => tourItem?.tour_id === tourId
        )
    );

    const newSitePassIntegrationEnabled = useMemo(() => {
        return !isEmpty(tour?.site_pass_integration?.facility_id);
    }, [tour?.site_pass_integration?.facility_id]);

    const sitePassReservationsExist = useMemo(() => {
        return !isEmpty(sitePassReservations);
    }, [sitePassReservations]);

    const parkpassId = useMemo(() => {
        if (!isEmpty(tour?.site_pass_integration?.facility_id)) {
            return tour?.site_pass_integration?.facility_id;
        }
        return FACILITY_SITEPASS[facilityId];
    }, [tour?.site_pass_integration?.facility_id, facilityId]);

    const isFacilitySitePassMapped = useMemo(() => {
        return !!FACILITY_SITEPASS[facilityId];
    }, [facilityId]);

    const sitePassValidationProblems = useSelector(
        validationSelectors.getValidationProblems
    );

    const isReservationConfirmed =
        reservation?.status === 'DELIVERED' || reservation?.status === 'CONFIRMED';

    const currentDate = reservation.tour_date;

    //Check whether we already have a valid pass to apply, for the selected date
    const availableSitePassForSelectedDate = useMemo(() => {
        if (isEmpty(selectedDate)) {
            return '';
        }
        const date = selectedDate?.format('YYYY-MM-DD');
        for (let i = 0; i < myPasses?.passes?.length; i += 1) {
            const pass = myPasses.passes[i];
            if (
                date >= pass.valid_from &&
                date <= pass.valid_to &&
                isEmpty(pass?.invalidation_type) &&
                pass.facility_id === parkpassId
            ) {
                return pass.id;
            }
        }
        for (let j = 0; j < cartReservations?.length; j += 1) {
            const orderDetails = cartReservations[j].order_details;
            if (
                date >= orderDetails.valid_from &&
                date <= orderDetails.valid_to &&
                orderDetails.facility_id === parkpassId
            ) {
                return cartReservations[j].reservation_id;
            }
        }

        return '';
    }, [myPasses, selectedDate, parkpassId, cartReservations]);

    //Check whether we already have a valid pass to apply , for the current date. value
    //only applies for reservation that are already confirmed
    const sitePassForCurrentDate = useMemo(() => {
        if (!isReservationConfirmed) {
            return '';
        }

        for (let i = 0; i < myPasses?.passes?.length; i += 1) {
            const pass = myPasses.passes[i];
            if (
                currentDate >= pass.valid_from &&
                currentDate <= pass.valid_to &&
                isEmpty(pass?.invalidation_type) &&
                pass.facility_id === parkpassId
            ) {
                return pass.id;
            }
        }
        for (let j = 0; j < cartReservations?.length; j += 1) {
            const orderDetails = cartReservations[j].order_details;
            if (
                currentDate >= orderDetails.valid_from &&
                currentDate <= orderDetails.valid_to &&
                orderDetails.facility_id === parkpassId
            ) {
                return cartReservations[j].reservation_id;
            }
        }

        return '';
    }, [myPasses, isReservationConfirmed, currentDate, parkpassId, cartReservations]);

    const allSitePassTypes = useSelector(passTypesSelectors.getPassTypes);
    const sitePassFacility = useSelector(
        facilityByIdOrSlugSelectors.getFacilityByIdOrSlug
    );

    const amountFromCart =
        orderDetailsData?.reservation?.pending_modifications?.amount_from_cart;

    //If the current total is greater than the previous payment then we should go to cart page
    const hasPendingPayment =
        orderDetailsData?.price_summary?.total_cost >
        orderDetailsData?.price_summary?.previous_payments;

    const hasPendingModification =
        !isEmpty(reservation?.pending_modifications) ||
        !!hasBeenModified ||
        !!hasPendingPayment;
    const [isSpinning, setSpinning] = useState(false);
    const [apiError, setApiError] = useState();
    const [useCartlessConfirmation, setUseCartlessConfirmation] = useState(false);
    const [isChallengeModalOpen, setIsChallengeModalOpen] = useState(false);
    const {
        getToken,
        handleRecaptchaError,
        isChallengeRequired,
        isV2Invalid,
        recaptchaCheckboxProps,
        resetChallenge,
    } = useRecaptchaWithFallback();

    const imageUrl = tourImagesData?.[`${facilityId}_${tourId}`]?.previewImage?.url;
    const proceedToCartButtonText = useRef('Proceed to Cart');
    const isLoading = useMemo(() => {
        return (
            (reservation && reservation?.reservation_id !== reservationId) ||
            isOrderDetailsLoading ||
            isToursLoading ||
            isFacilityDataLoading
        );
    }, [
        isFacilityDataLoading,
        isOrderDetailsLoading,
        isToursLoading,
        reservation,
        reservationId,
    ]);

    const shouldShowSitePasses = useMemo(() => {
        return (
            facilityId &&
            showSitePasses(
                facilityId,
                facilitiesUsingSitePassIntegration,
                newSitePassIntegrationEnabled
            ) &&
            !!useSitePass &&
            !tour?.is_commercial_quota
        );
    }, [
        facilitiesUsingSitePassIntegration,
        facilityId,
        tour,
        useSitePass,
        newSitePassIntegrationEnabled,
    ]);

    const purchasableSitePassTypes = useMemo(() => {
        return (allSitePassTypes || []).filter(
            (item) =>
                !HIDDEN_SITE_PASS_TYPES?.some((passType) =>
                    item?.sku?.endsWith(passType)
                ) && !item?.features?.is_field_sales
        );
    }, [allSitePassTypes]);

    const selectedDateString = useMemo(() => {
        if (isEmpty(selectedDate)) {
            return '';
        }
        return selectedDate?.format?.('YYYY-MM-DD') || selectedDate;
    }, [selectedDate]);

    const isSitePassPurchasableForSelectedDate = useMemo(() => {
        return isSitePassPurchasableOnDate(
            sitePassFacility,
            purchasableSitePassTypes,
            selectedDateString
        );
    }, [sitePassFacility, purchasableSitePassTypes, selectedDateString]);

    const sitePassNotRequiredForDate = useMemo(() => {
        return (
            !!shouldShowSitePasses &&
            !!parkpassId &&
            !isSitePassPurchasableForSelectedDate
        );
    }, [shouldShowSitePasses, parkpassId, isSitePassPurchasableForSelectedDate]);

    //Determine whether a user must buy a pass when applying a modification
    const userMustBuyAPassOnMod = useMemo(() => {
        return (
            isReservationConfirmed &&
            isEmpty(sitePassForCurrentDate) &&
            isEmpty(availableSitePassForSelectedDate) &&
            newSitePassIntegrationEnabled &&
            isSitePassPurchasableForSelectedDate
        );
    }, [
        availableSitePassForSelectedDate,
        isReservationConfirmed,
        isSitePassPurchasableForSelectedDate,
        sitePassForCurrentDate,
        newSitePassIntegrationEnabled,
    ]);

    //Determine whether a user must buy pass when making a purchase
    const userMustBuyAPassOnPurchase = useMemo(() => {
        return (
            !isReservationConfirmed &&
            isEmpty(availableSitePassForSelectedDate) &&
            newSitePassIntegrationEnabled &&
            isSitePassPurchasableForSelectedDate
        );
    }, [
        availableSitePassForSelectedDate,
        isReservationConfirmed,
        isSitePassPurchasableForSelectedDate,
        newSitePassIntegrationEnabled,
    ]);

    const sitePassTypes = useMemo(() => {
        const hasPassTypes = !!allSitePassTypes?.length;

        if (!hasPassTypes) {
            return allSitePassTypes;
        }

        const passTypes = allSitePassTypes.filter((item) => {
            const isFieldSales = !!item?.features?.is_field_sales;
            return !isFieldSales;
        });

        return passTypes;
    }, [allSitePassTypes]);

    useEffect(() => {
        if (shouldShowSitePasses && parkpassId) {
            dispatch(fetchFacilityByIdOrSlug(parkpassId));
            dispatch(fetchPassTypes(parkpassId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShowSitePasses, parkpassId]);

    useEffect(() => {
        let isMounted = true;

        if (isMounted) {
            //Means the user must buy a pass
            if (userMustBuyAPassOnMod) {
                setUseCartlessConfirmation(false);
                proceedToCartButtonText.current = 'Proceed to Cart';
                isMounted = false;
                return;
            }
            if (hasPendingPayment) {
                setUseCartlessConfirmation(false);
                proceedToCartButtonText.current = 'Proceed to Cart';
            } else if (!isNil(amountFromCart) && amountFromCart <= 0) {
                setUseCartlessConfirmation(true);
                proceedToCartButtonText.current = 'Apply Changes';
            } else if (
                isReservationConfirmed &&
                !amountFromCart &&
                hasPendingModification
            ) {
                setUseCartlessConfirmation(true);
                proceedToCartButtonText.current = 'Apply Changes';
            } else if (isReservationConfirmed && !hasPendingModification) {
                setUseCartlessConfirmation(true);
                proceedToCartButtonText.current = 'Apply Changes';
            } else {
                setUseCartlessConfirmation(false);
                proceedToCartButtonText.current = 'Proceed to Cart';
            }
        }

        return () => {
            isMounted = false;
        };
    }, [
        isReservationConfirmed,
        hasPendingPayment,
        amountFromCart,
        hasPendingModification,
        userMustBuyAPassOnMod,
    ]);

    useEffect(() => {
        /**
         * Clean sitePass reservations
         */
        dispatch(resetSitePass());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(getOrderDetailsView(reservationId));
        dispatch(CartActions.fetchCart());
        if (shouldShowSitePasses) {
            dispatch(getMyPasses());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservationId, shouldShowSitePasses]);

    useEffect(() => {
        if (facilityId && reservation && reservation?.reservation_id === reservationId) {
            dispatch(fetchFacilityData(facilityId));
            dispatch(fetchToursByFacility(facilityId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityId, reservation, reservationId]);

    /**
     * We set the site_pass_info_checked to true if facility doesn't allow sitePasses of if We are in modifying an existing reservation
     */
    useEffect(() => {
        if (
            !reservation?.site_pass_info_checked &&
            facilityId &&
            facilitiesUsingSitePassIntegration &&
            (!showSitePasses(facilityId, facilitiesUsingSitePassIntegration) ||
                !useSitePass)
        ) {
            dispatch(toggleSitePassInfoCheckbox(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        facilitiesUsingSitePassIntegration,
        facilityId,
        reservation?.site_pass_info_checked,
        useSitePass,
    ]);

    /**
     * Retrieve all my reservations for a given date when the sitePass integration is enabled
     */
    useEffect(() => {
        if (newSitePassIntegrationEnabled && currentDate && facilityId) {
            dispatch(getMyReservations(currentDate, facilityId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newSitePassIntegrationEnabled, currentDate, facilityId]);

    const hasAnotherReservationForCurrentDate = useMemo(() => {
        if (isEmpty(myReservations)) return false;

        for (let i = 0; i < myReservations.length; i += 1) {
            const anotherRes = myReservations[i];
            if (reservationId !== anotherRes.reservation_id) {
                return true;
            }
        }

        return false;
    }, [reservationId, myReservations]);

    const validationErrors = useMemo(() => {
        if (!useOrderDetailsState?.updateErrors) {
            return validationErrorsRefs?.current;
        }

        setUseOrderDetailsState((prevState) => ({
            ...prevState,
            updateErrors: false,
        }));

        const newErrors = {};
        newErrors.GENERAL = [];
        const refs = Object.values(validationRefs?.current);

        refs?.forEach((ref) => {
            if (!ref) return;
            const validation = ref?.validate();

            if (validation?.isInvalid) {
                newErrors.GENERAL.push({
                    inputId: validation?.id,
                    errorText: validation?.errorText,
                });
            }
        });

        if (!newErrors?.GENERAL?.length) {
            delete newErrors?.GENERAL;
        }

        if (sitePassReservations?.length > 0) {
            sitePassValidationProblems?.forEach((errors, index) => {
                if (!errors || errors?.length === 0) {
                    return;
                }
                let passName = '';
                let passType = null;
                if (sitePassTypes) {
                    passType = sitePassTypes.find(
                        ({ id }) => id === sitePassReservations[index]?.pass_type_id
                    );
                    if (passType && passType.name) {
                        passName = passType.name;
                    }
                }
                newErrors[errorSectionHeader(index + 1, passName)] = errors.map((error) =>
                    errorObject(error, index + 1, passType)
                );
            });

            // Frontend site pass validations that the backend reservation validation does not cover (Mirroring ui-sitepass)
            const facilityHasNonResidentFees = sitePassTypes?.some(
                (passType) => passType?.features?.non_resident_fee
            );

            sitePassReservations.forEach((item, index) => {
                const passType = sitePassTypes?.find(
                    ({ id }) => id === sitePassReservations[index]?.pass_type_id
                );
                const sectionHeader = errorSectionHeader(index + 1, passType?.name);

                const addError = (message) => {
                    newErrors[sectionHeader] = [
                        ...(newErrors[sectionHeader] || []),
                        errorObject(message, index + 1, passType),
                    ];
                };

                if (
                    item?.holder_info?.email_confirmation &&
                    item?.holder_info?.email !== item?.holder_info?.email_confirmation
                ) {
                    addError('Email Confirmation: Email and confirmation do not match.');
                }

                if (
                    passType?.features?.non_resident_fee?.fee &&
                    !item?.features?.non_resident_fee?.answered
                ) {
                    addError(
                        'non_resident_selector: Please answer the U.S. citizenship / residency question to proceed to cart'
                    );
                }

                if (!item?.holder_info?.zip_code) {
                    addError('Zip Code: Zip Code: Must not be empty.');
                } else if (
                    facilityHasNonResidentFees &&
                    passType?.duration === 'annual' &&
                    !zipCodeValidator(item?.holder_info?.zip_code).isValid
                ) {
                    addError('Zip Code: A valid U.S. Zip Code is required');
                }
            });
        }

        validationErrorsRefs.current = newErrors;

        return validationErrorsRefs.current;
    }, [
        sitePassReservations,
        sitePassTypes,
        sitePassValidationProblems,
        useOrderDetailsState?.updateErrors,
    ]);

    const addValidationRef = (refId) => (ref) => {
        validationRefs.current[refId] = ref;
    };

    const redirect = useCallback(
        (url) => {
            if (url) {
                if (isOwnService) {
                    navigate(url);
                    return;
                }

                window.location.href = url;
            }
        },
        [isOwnService, navigate]
    );

    const handleModifyClick = useCallback(() => {
        dispatch(openModifyModal());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRemoveClick = useCallback(async () => {
        if (reservation?.status === 'DRAFT' || reservation?.status === 'HOLD') {
            await dispatch(removeFromCart(reservationId));
            dispatch(CartActions?.fetchCart());
            redirect(`/timed-entry/${facilityId}`);
        } else if (
            reservation?.status === 'DELIVERED' ||
            reservation?.status === 'CONFIRMED'
        ) {
            const resInCart = cartReservations?.find(
                (item) => item?.reservation_id === reservationId
            );

            if (resInCart) {
                try {
                    await dispatch(removeFromCart(reservationId));
                    window.location = '/account/reservations';
                } catch (error) {
                    window.location = '/account/reservations';
                }
            } else if (isReservationConfirmed && !!reservation?.pending_modifications) {
                await dispatch(discardPendingModifications(reservationId));
                if (reservation?.order_id) {
                    window.location = `/account/orders/${reservation?.order_id}/reservations/${reservationId}/tickets/viewReservation`;
                } else {
                    window.location = '/account/reservations';
                }
            } else {
                window.location = '/account/reservations';
            }
        } else {
            redirect(`/timed-entry/${facilityId}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        cartReservations,
        facilityId,
        isReservationConfirmed,
        reservation.order_id,
        reservation.pending_modifications,
        reservation.status,
        reservationId,
    ]);

    const validate = useCallback(() => {
        const newErrors = {};
        newErrors.GENERAL = [];
        const refs = Object.values(validationRefs?.current);

        refs?.forEach((ref) => {
            if (!ref) return;
            const validation = ref.validate();

            if (validation?.isInvalid) {
                newErrors.GENERAL.push({
                    inputId: validation.id,
                    errorText: validation.errorText,
                });
            }
        });

        if (newErrors?.GENERAL.length > 0) {
            return false;
        }

        return true;
    }, []);

    const validateSitePassReservations = useCallback(async () => {
        const errorsCheck = await Promise.all(
            sitePassReservations?.map(async (res, index) => {
                return dispatch(validateReservation(index, res));
            })
        );
        return errorsCheck.filter((item) => !isNil(item));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sitePassReservations]);

    const validateSitePassOtherFields = useCallback(() => {
        if (isEmpty(sitePassReservations)) {
            return true;
        }

        const facilityHasNonResidentFees = sitePassTypes?.some(
            (passType) => passType?.features?.non_resident_fee
        );

        return sitePassReservations.every((item) => {
            const passType = sitePassTypes?.find(({ id }) => id === item?.pass_type_id);

            if (
                passType?.features?.non_resident_fee?.fee &&
                !item?.features?.non_resident_fee?.answered
            ) {
                return false;
            }

            if (
                item?.holder_info?.email_confirmation &&
                item?.holder_info?.email !== item?.holder_info?.email_confirmation
            ) {
                return false;
            }

            if (!item?.holder_info?.zip_code) {
                return false;
            }
            if (
                facilityHasNonResidentFees &&
                passType?.duration === 'annual' &&
                !zipCodeValidator(item?.holder_info?.zip_code).isValid
            ) {
                return false;
            }

            return true;
        });
    }, [sitePassReservations, sitePassTypes]);

    // Create the site pass reservation(s) with reCAPTCHA verification.
    // Returns a status the caller uses to decide whether to navigate:
    //  - 'success':   reservation created, safe to proceed to cart
    //  - 'challenge': backend requested the v2 checkbox; fallback modal opened
    //  - 'failed':    reCAPTCHA hard-failed; an error notification was shown
    // Non-reCAPTCHA errors are re-thrown so handleProceedToCart's catch can
    // surface them via apiError (and not navigate on a failed reservation).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const createSitePassReservations = useCallback(async () => {
        try {
            let newSitePassReservations = [];

            // Delete pending `id`s before sending to server.
            newSitePassReservations = sitePassReservations.map((sitePassReservation) => {
                const newSitePassReservation = { ...sitePassReservation };
                if (newSitePassReservation?.is_pending) {
                    delete newSitePassReservation?.id;
                }

                const passType = sitePassTypes.find(
                    ({ id }) => id === newSitePassReservation?.pass_type_id
                );
                const acceptedFeatures = passType?.features?.accepted ?? [];
                if (acceptedFeatures.includes('need_to_know')) {
                    Object.assign(newSitePassReservation?.features, {
                        accepted: { need_to_know: true },
                    });
                }

                return newSitePassReservation;
            });

            let recaptchaToken = null;
            try {
                recaptchaToken = await getToken('create_sitepass_reservation');
            } catch {
                recaptchaToken = null;
            }

            const recaptchaOptions = {
                recaptcha_token: recaptchaToken,
                recaptcha_version: isChallengeRequired ? 'V2' : 'V3',
            };

            // Will also update reservations.
            await dispatch(createReservations(newSitePassReservations, recaptchaOptions));

            // Reservation succeeded, so tear down any active challenge fallback.
            if (isChallengeModalOpen || isChallengeRequired) {
                resetChallenge();
            }
            setIsChallengeModalOpen(false);

            // Remove any IDs the user removed via the pass counters.
            sitePassRemovedReservationIds?.reduce(async (promise, id) => {
                await promise;
                return dispatch(removeReservation(id));
            }, Promise.resolve());

            return 'success';
        } catch (error) {
            const { shouldRetry, isFailed } = handleRecaptchaError(error);

            // The invisible/score assessment failed and the service is asking
            // for the visible checkbox challenge. handleRecaptchaError has
            // already flipped the hook into v2 mode; open the fallback modal
            // instead of surfacing an error. Solving it re-submits with a V2
            // token via handleChallengeContinue.
            if (shouldRetry) {
                setIsChallengeModalOpen(true);
                return 'challenge';
            }

            // reCAPTCHA verification hard-failed. Surface a distinct message
            // depending on which step failed: RECAPTCHA_ERROR2 when the user's
            // checkbox challenge (V2) submission was rejected, otherwise
            // RECAPTCHA_ERROR for the invisible/enterprise assessment failure.
            if (isFailed) {
                const failedCheckbox = isChallengeRequired;
                resetChallenge();
                setIsChallengeModalOpen(false);
                dispatch(
                    NotificationActions.setNotification(
                        'error',
                        failedCheckbox ? RECAPTCHA_ERROR2 : RECAPTCHA_ERROR
                    )
                );
                window.scrollTo(0, 0);
                return 'failed';
            }

            // Not a reCAPTCHA control signal: let the caller surface it as a
            // normal API error rather than navigating on a failed reservation.
            throw error;
        }
    });

    const handleApplyModifications = useCallback(async () => {
        setSpinning(true);

        const pendingMods = reservation?.pending_modifications || {};

        if (isReservationConfirmed && newSitePassIntegrationEnabled) {
            if (!hasAnotherReservationForCurrentDate) {
                if (isEmpty(availableSitePassForSelectedDate)) {
                    pendingMods.site_pass_info = {
                        pass_id: sitePassForCurrentDate,
                        update: true, //We need to tell the backend to update the current site pass to a newer date
                        valid_from: pendingMods.tour_date,
                    };
                }
            }

            if (hasAnotherReservationForCurrentDate) {
                if (isEmpty(availableSitePassForSelectedDate)) {
                    pendingMods.site_pass_info = {
                        pass_id: sitePassForCurrentDate,
                        update: true, //We need to tell the backend to update the current site pass to a newer date
                        valid_from: pendingMods.tour_date,
                    };
                }
            }
        }

        try {
            // If user checked box, send data to account service
            if (shouldSaveToAccount) {
                await dispatch(saveChangesToAccount(ticketHolderDetails));
            }

            await dispatch(
                updateWithTicketHolderDetails(reservation.reservation_id, pendingMods)
            );
            await dispatch(applyPendingModifications(reservation.reservation_id));
            dispatch(getOrderDetailsView(reservation.reservation_id));
            dispatch(fetchFacilityData(reservation.reservation_id));
            setSpinning(false);
            redirect(`/timed-entry/confirmation/${reservation.reservation_id}`);
        } catch (error) {
            let errorMessage = error.message;
            if (
                error?.response?.data?.error ===
                'guest accounts are limited to one (1) reservation per day'
            ) {
                errorMessage =
                    'The email you provided has already been used on a previous reservation or is in your cart. Guests are limited to one reservation per day.';
            } else if (
                error?.response?.data?.error ===
                'validation: Email: must be a valid email address.'
            ) {
                errorMessage = 'Email: must be a valid email address.';
            }
            setSpinning(false);
            setApiError(errorMessage);
        }
    }, [
        reservation?.pending_modifications,
        reservation.reservation_id,
        isReservationConfirmed,
        newSitePassIntegrationEnabled,
        hasAnotherReservationForCurrentDate,
        availableSitePassForSelectedDate,
        sitePassForCurrentDate,
        shouldSaveToAccount,
        dispatch,
        redirect,
        ticketHolderDetails,
    ]);

    const handleProceedToCart = useCallback(
        async ({ navigate: shouldNavigate = true } = {}) => {
            try {
                if (shouldShowSitePasses) {
                    const errorsResp = await validateSitePassReservations();
                    const otherFieldsValid = validateSitePassOtherFields();
                    if (errorsResp.length > 0 || !otherFieldsValid) {
                        setUseOrderDetailsState((prevState) => ({
                            ...prevState,
                            updateErrors: true,
                        }));
                        return;
                    }
                }

                const isValid = validate();

                // If the form isn't valid, we abort.
                if (!isValid) {
                    setUseOrderDetailsState((prevState) => ({
                        ...prevState,
                        updateErrors: true,
                    }));
                    return;
                }

                //flow should be different for this case, the WarningPassModal.jsx will handle the rest
                if (
                    newSitePassIntegrationEnabled &&
                    isReservationConfirmed &&
                    !userMustBuyAPassOnMod &&
                    !sitePassNotRequiredForDate
                ) {
                    dispatch(openWarningPassModal());
                    return;
                }

                setSpinning(true);

                const pendingMods = reservation?.pending_modifications || {};

                // If user checked box, send data to account service
                if (shouldSaveToAccount) {
                    await dispatch(saveChangesToAccount(ticketHolderDetails));
                }

                if (useCartlessConfirmation) {
                    await dispatch(
                        updateWithTicketHolderDetails(reservationId, pendingMods)
                    );
                    await dispatch(applyPendingModifications(reservationId));
                    dispatch(getOrderDetailsView(reservationId));
                    dispatch(fetchFacilityData(facilityId));
                    setSpinning(false);
                    if (shouldNavigate) {
                        redirect(`/timed-entry/confirmation/${reservationId}`);
                    }
                    return true;
                }
                await dispatch(updateWithTicketHolderDetails(reservationId, pendingMods));

                //If there was no pending modifications, but the users had to buy a pass, we must apply the modifications
                //Since cart will not have any modifications for TimedEntry,
                if (isEmpty(pendingMods) && userMustBuyAPassOnMod) {
                    await dispatch(applyPendingModifications(reservationId));
                }

                if (shouldShowSitePasses) {
                    const sitePassStatus = await createSitePassReservations();
                    // A reCAPTCHA challenge (v2 fallback modal opened) or a hard
                    // failure means the reservation did not go through. Stop the
                    // spinner and abort without navigating; the user resolves the
                    // challenge via handleChallengeContinue, or sees the error
                    // notification. Returning a non-true value also signals Buy
                    // Now to abort.
                    if (sitePassStatus !== 'success') {
                        setSpinning(false);
                        return false;
                    }
                }
                setSpinning(false);
                if (shouldNavigate) {
                    window.location = '/cart';
                }
                return true;
            } catch (error) {
                let errorMessage = error?.response?.data?.error || error.message;
                if (
                    error?.response?.data?.error ===
                    'guest accounts are limited to one (1) reservation per day'
                ) {
                    errorMessage =
                        'The email you provided has already been used on a previous reservation or is in your cart. Guests are limited to one reservation per day.';
                } else if (
                    error?.response?.data?.error ===
                    'validation: Email: must be a valid email address.'
                ) {
                    errorMessage = 'Email: must be a valid email address.';
                }
                setSpinning(false);
                setApiError(errorMessage);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [
            createSitePassReservations,
            facilityId,
            redirect,
            reservation?.pending_modifications,
            reservationId,
            shouldSaveToAccount,
            shouldShowSitePasses,
            ticketHolderDetails,
            useCartlessConfirmation,
            validate,
            validateSitePassReservations,
            validateSitePassOtherFields,
            tour,
            isReservationConfirmed,
            userMustBuyAPassOnMod,
            sitePassNotRequiredForDate,
        ]
    );

    // Called by the v2 fallback modal's "Continue" button once the user has
    // solved the checkbox. Re-runs only the site pass creation (the pre-steps
    // already ran on the initial Proceed to Cart) and navigates on success.
    const handleChallengeContinue = useCallback(async () => {
        setSpinning(true);
        try {
            const sitePassStatus = await createSitePassReservations();
            if (sitePassStatus === 'success') {
                setSpinning(false);
                window.location = '/cart';
                return;
            }
            // 'challenge' (checkbox still unsolved/invalid) keeps the modal open;
            // 'failed' shows the notification and closes it inside
            // createSitePassReservations. Either way, stop the spinner.
            setSpinning(false);
        } catch (error) {
            setSpinning(false);
            setApiError(error?.response?.data?.error || error.message);
        }
    }, [createSitePassReservations]);

    const handleContinueShopping = useCallback(async () => {
        const pendingMods = reservation?.pending_modifications || {};
        setSpinning(true);
        dispatch(updateWithTicketHolderDetails(reservationId, pendingMods))
            .then(() => {
                setSpinning(false);
                redirect(`/timed-entry/${facilityId}`);
            })
            .catch((error) => {
                setSpinning(false);
                setApiError(error.message);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityId, reservation.pending_modifications, reservationId]);

    const handleToggleNeedToKnowCheckbox = useCallback(() => {
        dispatch(toggleNeedToKnowCheckbox());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleToggleSitePassInfoCheckbox = useCallback(() => {
        dispatch(toggleSitePassInfoCheckbox());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        facilityId,
        imageUrl,
        acceptsPasses: tour?.accepts_passes,
        isLoading,
        orderDetailsData,
        isReservationConfirmed,
        hasPendingModification,
        handleModifyClick,
        handleRemoveClick,
        isNeedToKnowChecked,
        isSitePassInfoChecked,
        proceedToCartButtonText: proceedToCartButtonText?.current,
        isSpinning,
        addValidationRef,
        validationErrors,
        apiError,
        handleToggleNeedToKnowCheckbox,
        handleToggleSitePassInfoCheckbox,
        handleProceedToCart,
        handleContinueShopping,
        isChallengeModalOpen,
        recaptchaCheckboxProps,
        isV2Invalid,
        handleChallengeContinue,
        tour,
        reservation,
        cartReservations,
        shouldShowSitePasses,
        availableSitePassForSelectedDate,
        hasAnotherReservationForCurrentDate,
        handleApplyModifications,
        sitePassForCurrentDate,
        sitePassReservationsExist,
        userMustBuyAPassOnMod,
        userMustBuyAPassOnPurchase,
        sitePassNotRequiredForDate,
        isFacilitySitePassMapped,
        newSitePassIntegrationEnabled,
        showDigitalPassDiscounts,
    };
};

export default useOrderDetails;
